import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import StoreSidebar from "../components/StoreSidebar";
import { db } from "../firebase/firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { 
  collection, 
  onSnapshot, 
  query,
  where,
  doc, 
  addDoc, 
  deleteDoc 
} from "firebase/firestore";
import {
  FileText, User, Search, Plus, Trash2, IndianRupee, AlertCircle, 
  MapPin, RefreshCw, Layers, History, Calendar, Filter, 
  TrendingDown, CheckCircle2, DollarSign, Tag, Briefcase
} from "lucide-react";

export default function ExpensesMatrix() {
  // --- TOP SYSTEM NAV TAB CONTROLLERS ---
  const [activeTab, setActiveTab] = useState("addExpense"); 
  const [activeBranchId, setActiveBranchId] = useState(""); 
  const [activeStoreName, setActiveStoreName] = useState("Loading Store...");

  // --- REGISTRY DATA REPOSITORIES ---
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState(["Store Rent", "Salaries", "Utility Bills", "Lab Equipment", "Marketing"]);
  const [storeEmployees, setStoreEmployees] = useState([]); 
  const [isProcessing, setIsProcessing] = useState(false);
  const [systemError, setSystemError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // --- FILTER STATES FOR HISTORY LOGS ---
  const [filterCategory, setFilterCategory] = useState("All Categories");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // --- FORM STATES: ADD EXPENSE ---
  const [expenseTitle, setExpenseTitle] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [chosenCategory, setChosenCategory] = useState("Store Rent");
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split("T")[0]);
  const [spentBy, setSpentBy] = useState("");
  const [paymentMode, setPaymentMode] = useState("UPI / Digital");
  const [expenseNotes, setExpenseNotes] = useState("");

  // --- FORM STATES: NEW CATEGORY ---
  const [newCategoryName, setNewCategoryName] = useState("");

  // --- 1. DETECT USER UID & FETCH MATCHING STORE ---
  useEffect(() => {
    const auth = getAuth();
    let unsubStores = () => {};

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Query stores collection where linkedAuthUid matches the user's uid
        const storeQuery = query(
          collection(db, "stores"), 
          where("linkedAuthUid", "==", user.uid)
        );
        
        unsubStores = onSnapshot(storeQuery, (snapshot) => {
          if (!snapshot.empty) {
            const storeDoc = snapshot.docs[0]; 
            const data = storeDoc.data();
            
            // Set branch ID to trigger subsequent data fetches
            setActiveBranchId(storeDoc.id);
            setActiveStoreName(data.storeId || "Unnamed Store");
          } else {
            console.warn("No matching store found for this user UID.");
            setActiveBranchId("");
            setActiveStoreName("No Assigned Store");
          }
        });
      } else {
        setActiveBranchId("");
        setActiveStoreName("Please Log In");
      }
    });

    return () => {
      unsubAuth();
      unsubStores();
    };
  }, []);

  // --- 2. ATTACH REAL-TIME NESTED EVENT SUBSCRIBERS ---
  useEffect(() => {
    if (!activeBranchId) return;

    // Listens to deep nested expenses path
    const nestedExpensesRef = collection(db, "stores", activeBranchId, "expenses");
    const unsubExpenses = onSnapshot(nestedExpensesRef, (snapshot) => {
      setExpenses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Listens to deep nested employees path
    const nestedEmployeesRef = collection(db, "stores", activeBranchId, "employees");
    const unsubStaff = onSnapshot(nestedEmployeesRef, (snapshot) => {
      setStoreEmployees(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Global categories remain independent of branch
    const unsubCategories = onSnapshot(collection(db, "expense_categories"), (snapshot) => {
      if (!snapshot.empty) {
        setCategories(snapshot.docs.map(doc => doc.data().name));
      }
    });

    return () => { unsubExpenses(); unsubStaff(); unsubCategories(); };
  }, [activeBranchId]);

  // --- DYNAMIC FILTERED SPREADSHEET MATRIX ---
  const processedExpenses = useMemo(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split("T")[0];

    return expenses.filter(exp => {
      if (filterCategory !== "All Categories" && exp.category !== filterCategory) return false;
      
      const targetDate = exp.date;
      if (!targetDate) return true;

      // Extract by Calendar Range
      if (startDate || endDate) {
        if (startDate && targetDate < startDate) return false;
        if (endDate && targetDate > endDate) return false;
      } else {
        // Defaults to current month if no dates selected
        if (targetDate < firstDayOfMonth || targetDate > lastDayOfMonth) return false;
      }

      return true;
    });
  }, [expenses, filterCategory, startDate, endDate]);

  // --- LIVE ARITHMETIC FINANCIAL METRICS ---
  const financialTotals = useMemo(() => {
    let grandTotal = 0; let upiTotal = 0; let cashTotal = 0; let cardTotal = 0;

    processedExpenses.forEach(exp => {
      const amt = Number(exp.amount) || 0;
      grandTotal += amt;
      if (exp.paymentMethod === "UPI / Digital") upiTotal += amt;
      else if (exp.paymentMethod === "Cash Liquidity") cashTotal += amt;
      else if (exp.paymentMethod === "Card Settlement") cardTotal += amt;
    });

    return { grandTotal, upiTotal, cashTotal, cardTotal };
  }, [processedExpenses]);

  // --- BACKEND WRITE HANDLERS ---
  const handleCommitExpense = async (e) => {
    e.preventDefault();
    if (!activeBranchId) {
      setSystemError("Operational Error: Missing targeted root branch workspace key.");
      return;
    }
    if (!expenseTitle.trim() || !expenseAmount || !spentBy) {
      setSystemError("Validation Intercept: Title, Amount, and Spent Authorized Employee parameters are mandatory.");
      return;
    }

    setIsProcessing(true); setSystemError(null); setSuccessMessage("");

    try {
      const payload = {
        title: expenseTitle.trim(),
        amount: Number(expenseAmount),
        category: chosenCategory,
        date: expenseDate,
        spentBy, 
        paymentMethod: paymentMode,
        notes: expenseNotes.trim(),
        branchId: activeBranchId,
        timestamp: new Date().toISOString()
      };

      await addDoc(collection(db, "stores", activeBranchId, "expenses"), payload);
      
      setSuccessMessage(`Expense safely pushed into storefront infrastructure ledger.`);
      setExpenseTitle(""); setExpenseAmount(""); setSpentBy(""); setExpenseNotes("");
    } catch (err) {
      setSystemError(err.message || "Error saving transaction payload to Firebase.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    const cleanName = newCategoryName.trim();
    if (!cleanName) return;

    if (categories.some(cat => cat.toLowerCase() === cleanName.toLowerCase())) {
      alert("Validation Warning: Expenditure category string already active.");
      return;
    }

    try {
      await addDoc(collection(db, "expense_categories"), { name: cleanName });
      setNewCategoryName("");
      alert("New operational expense ledger index initiated safely.");
    } catch (err) {
      console.error("Error creating category code row: ", err);
    }
  };

  const handlePurgeExpense = async (id) => {
    if (!window.confirm("ERP Destructive Warning: Wiping this item permanently from core cloud logs?")) return;
    try {
      await deleteDoc(doc(db, "stores", activeBranchId, "expenses", id));
    } catch (err) {
      console.error("Failure dropping database index object line: ", err);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800 font-sans antialiased selection:bg-blue-200">
      <StoreSidebar />

      <div className="flex-1 p-4 lg:p-8 space-y-6 overflow-x-hidden">
        
        {/* RE-ARCHITECTED HEADER CONTROL PANEL */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between p-6 bg-white border border-slate-200 rounded-3xl gap-6 shadow-sm">
          <div>
            <h1 className="text-xl md:text-2xl font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <TrendingDown className="text-blue-600" size={24} /> Expense Tracking Matrix
            </h1>
            <div className="flex items-center gap-2 mt-2 text-xs font-bold text-slate-500">
              <MapPin size={14} className="text-blue-500" />
              <span>Operating Branch: </span>
              {/* Replaced dropdown with read-only matched store block */}
              <div className="bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200 text-blue-700 font-black">
                {activeStoreName}
              </div>
            </div>
          </div>

          <div className="flex bg-slate-100 p-1.5 rounded-2xl border border-slate-200 text-xs font-bold shadow-inner">
            <button onClick={() => setActiveTab("addExpense")} className={`px-5 py-2.5 rounded-xl transition-all duration-200 ${activeTab === "addExpense" ? "bg-white text-blue-600 shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-700"}`}>Record Expense</button>
            <button onClick={() => setActiveTab("expensesLog")} className={`px-5 py-2.5 rounded-xl transition-all duration-200 ${activeTab === "expensesLog" ? "bg-white text-blue-600 shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-700"}`}>Expenses Log</button>
            <button onClick={() => setActiveTab("categorySettings")} className={`px-5 py-2.5 rounded-xl transition-all duration-200 ${activeTab === "categorySettings" ? "bg-white text-blue-600 shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-700"}`}>Ledger Categories</button>
          </div>
        </div>

        {/* SYSTEM STATUS NOTIFIER ROW */}
        <AnimatePresence>
          {systemError && (
            <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-rose-600 text-xs font-bold flex items-center gap-2 shadow-sm"><AlertCircle size={16} /> {systemError}</motion.div>
          )}
          {successMessage && (
            <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-700 text-xs font-bold flex justify-between items-center shadow-sm">
              <span className="flex items-center gap-2"><CheckCircle2 size={16}/> {successMessage}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* COMPREHENSIVE SPLIT STRUCTURAL COLUMN MATRIX */}
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
          
          {/* LEFT PANELS VIEWPORT WORKSPACE */}
          <div className="xl:col-span-7 space-y-6">
            
            {activeTab === "addExpense" && (
              <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 space-y-6 shadow-sm">
                <h2 className="text-sm font-black uppercase tracking-wider text-slate-600 flex items-center gap-2 border-b border-slate-100 pb-3">
                  <Briefcase size={16} className="text-blue-500" /> Record Outbound Cashflow
                </h2>
                <form onSubmit={handleCommitExpense} className="grid grid-cols-1 md:grid-cols-2 gap-5 text-xs">
                  <div className="space-y-1.5">
                    <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Expense Title *</label>
                    <input type="text" placeholder="Ex: Shop Electrical Maintenance" value={expenseTitle} onChange={e => setExpenseTitle(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 outline-none focus:border-blue-500 focus:bg-white transition-colors" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Amount (₹) *</label>
                    <input type="number" placeholder="4500" value={expenseAmount} onChange={e => setExpenseAmount(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 font-mono font-bold outline-none focus:border-blue-500 focus:bg-white transition-colors" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Ledger Category</label>
                    <select value={chosenCategory} onChange={e => setChosenCategory(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 outline-none focus:border-blue-500 cursor-pointer">
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Transaction Date</label>
                    <input type="date" value={expenseDate} onChange={e => setExpenseDate(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 font-mono outline-none focus:border-blue-500 cursor-pointer" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Authorized Employee *</label>
                    <select value={spentBy} onChange={e => setSpentBy(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 font-bold text-slate-800 outline-none focus:border-blue-500 cursor-pointer">
                      <option value="">Select Spent Staff Member</option>
                      {storeEmployees.map(emp => <option key={emp.id} value={emp.name}>{emp.name} ({emp.role || "Staff"})</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Payment Method</label>
                    <select 
                      value={paymentMode} 
                      onChange={e => setPaymentMode(e.target.value)} 
                      className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 outline-none focus:border-blue-500 cursor-pointer"
                    >
                      <option>UPI / Digital</option>
                      <option>Cash Liquidity</option>
                      <option>Card Settlement</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 space-y-1.5">
                    <label className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Remarks / Notes</label>
                    <input type="text" placeholder="Add further explanatory details here..." value={expenseNotes} onChange={e => setExpenseNotes(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 outline-none focus:border-blue-500 focus:bg-white transition-colors" />
                  </div>
                  <div className="md:col-span-2 pt-2">
                    <button type="submit" disabled={isProcessing} className={`w-full py-4 rounded-xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all ${isProcessing ? "bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200" : "bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-200"}`}>
                      {isProcessing ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />} Commit Expenditure Record
                    </button>
                  </div>
                </form>
              </div>
            )}

            {activeTab === "expensesLog" && (
              <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 space-y-6 shadow-sm">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                  <h2 className="text-sm font-black uppercase tracking-wider text-slate-600 flex items-center gap-2">
                    <History size={16} className="text-blue-500" /> Auditable Journals
                  </h2>
                  
                  {/* CALENDAR & CATEGORY FILTERS */}
                  <div className="flex flex-wrap items-center gap-3 text-[11px] font-bold">
                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 p-2 rounded-xl focus-within:border-blue-500 transition-colors">
                      <Filter size={12} className="text-slate-400" />
                      <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="bg-transparent text-slate-700 outline-none cursor-pointer">
                        <option value="All Categories">All Categories</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>

                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 p-2 rounded-xl focus-within:border-blue-500 transition-colors">
                      <Calendar size={12} className="text-slate-400" />
                      <span className="text-slate-500">From:</span>
                      <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-transparent outline-none text-slate-700 font-mono cursor-pointer" />
                    </div>
                    
                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 p-2 rounded-xl focus-within:border-blue-500 transition-colors">
                      <Calendar size={12} className="text-slate-400" />
                      <span className="text-slate-500">To:</span>
                      <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-transparent outline-none text-slate-700 font-mono cursor-pointer" />
                    </div>
                    
                    {(startDate || endDate || filterCategory !== "All Categories") && (
                      <button onClick={() => { setStartDate(""); setEndDate(""); setFilterCategory("All Categories"); }} className="text-rose-500 hover:text-rose-600">Reset</button>
                    )}
                  </div>
                </div>

                <div className="overflow-x-auto text-[11px]">
                  <table className="w-full border-collapse text-left">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider">
                        <th className="p-3 pl-4">Expenditure Title</th>
                        <th className="p-3">Date</th>
                        <th className="p-3">Classification</th>
                        <th className="p-3">Agent User</th>
                        <th className="p-3 text-right">Debit Charge (₹)</th>
                        <th className="p-3 text-center">Purge</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                      {processedExpenses.length === 0 ? (
                        <tr><td colSpan="6" className="p-8 text-center text-slate-500">No transactions found matching the selected date or category.</td></tr>
                      ) : (
                        processedExpenses.map(exp => (
                          <tr key={exp.id} className="hover:bg-slate-50/80 transition-all">
                            <td className="p-3 pl-4 font-bold text-slate-800">
                              <span>{exp.title}</span>
                              {exp.notes && <span className="block text-[9px] text-slate-500 font-normal italic max-w-xs truncate mt-0.5">{exp.notes}</span>}
                            </td>
                            <td className="p-3 text-slate-600 font-mono">{exp.date}</td>
                            <td className="p-3">
                              <span className="px-2.5 py-1 rounded-md text-[9px] bg-slate-100 text-slate-600 border border-slate-200 uppercase font-black tracking-wider">{exp.category}</span>
                            </td>
                            <td className="p-3 text-slate-600">{exp.spentBy} <span className="block text-[9px] text-slate-400 mt-0.5">via {exp.paymentMethod}</span></td>
                            <td className="p-3 text-right font-black text-slate-800 font-mono">₹{exp.amount?.toLocaleString()}</td>
                            <td className="p-3 text-center">
                              <button onClick={() => handlePurgeExpense(exp.id)} className="text-slate-300 hover:bg-rose-50 hover:text-rose-500 p-2 rounded-lg transition-colors"><Trash2 size={14} /></button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "categorySettings" && (
              <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 space-y-6 shadow-sm">
                <div className="border-b border-slate-100 pb-4">
                  <h2 className="text-sm font-black uppercase tracking-wider text-slate-600 flex items-center gap-2"><Tag size={16} className="text-blue-500" /> Account Categories</h2>
                  <p className="text-slate-500 text-xs mt-1 font-medium">Append custom billing tags directly into global selection listings.</p>
                </div>

                <form onSubmit={handleCreateCategory} className="flex flex-col sm:flex-row gap-3 text-xs">
                  <input type="text" placeholder="Ex: Office Refreshments" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} className="flex-1 p-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-800 outline-none focus:bg-white focus:border-blue-500 transition-colors" />
                  <button type="submit" className="px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shrink-0">
                    <Plus size={16} /> Create Tag
                  </button>
                </form>

                <div className="space-y-3 pt-2">
                  <span className="text-[10px] uppercase tracking-widest text-slate-400 block font-black">Active Registry</span>
                  <div className="flex flex-wrap gap-2.5">
                    {categories.map((cat, i) => (
                      <span key={i} className="px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-xl text-xs font-bold text-blue-700 flex items-center gap-1.5 shadow-sm">
                        <span className="h-1.5 w-1.5 rounded-full bg-blue-500" /> {cat}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* RIGHT PANELS REVENUE OUTFLOW DESK */}
          <div className="xl:col-span-5 space-y-6">
            <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 space-y-6 shadow-sm">
              <h2 className="text-sm font-black uppercase tracking-wider text-slate-800 flex items-center gap-2 border-b border-slate-100 pb-3">
                <TrendingDown size={18} className="text-rose-500" /> Real-time Outflow Summary
              </h2>
              
              <div className="p-5 bg-rose-50 rounded-2xl border border-rose-100 flex items-center justify-between">
                <div>
                  <span className="text-[10px] text-rose-600 font-black uppercase tracking-widest block mb-1">
                    {startDate || endDate ? "Selected Scope Total" : "Current Month Outflow"}
                  </span>
                  <span className="block text-3xl font-black text-rose-600 font-mono tracking-tight">₹{financialTotals.grandTotal.toLocaleString()}</span>
                </div>
                <div className="p-3 bg-white border border-rose-200 text-rose-500 rounded-xl shadow-sm">
                  <IndianRupee size={24} />
                </div>
              </div>

              <div className="space-y-4 text-xs">
                <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">Split Settlement Ratios</span>
                
                <div className="space-y-1.5">
                  <div className="flex justify-between font-bold text-slate-600 text-[11px]">
                    <span>UPI / Digital Channels</span>
                    <span className="font-mono text-slate-800">₹{financialTotals.upiTotal.toLocaleString()}</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${financialTotals.grandTotal > 0 ? (financialTotals.upiTotal / financialTotals.grandTotal) * 100 : 0}%` }} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between font-bold text-slate-600 text-[11px]">
                    <span>Cash Liquidity</span>
                    <span className="font-mono text-slate-800">₹{financialTotals.cashTotal.toLocaleString()}</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${financialTotals.grandTotal > 0 ? (financialTotals.cashTotal / financialTotals.grandTotal) * 100 : 0}%` }} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between font-bold text-slate-600 text-[11px]">
                    <span>Card Settlement</span>
                    <span className="font-mono text-slate-800">₹{financialTotals.cardTotal.toLocaleString()}</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 transition-all duration-500" style={{ width: `${financialTotals.grandTotal > 0 ? (financialTotals.cardTotal / financialTotals.grandTotal) * 100 : 0}%` }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}